import { DashboardContext } from 'live/context/dashboard'
import { getSelectedCutoffsValues, saveCutoffsValues } from '../../services/api'
import React, { JSX, memo, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

export type CutOffsFilterProps = {
    closeFilterDropdown: () => void
    currentFilters: JSX.Element
    filterActions: JSX.Element
}

export const CutOffsFilterComponent = memo(function CutOffsFilterComponent({ currentFilters }: CutOffsFilterProps) {
    const context = useContext(DashboardContext)

    const [lowerCutoffSeconds, setLowerCutoffSeconds] = useState('')
    const [greaterCutoffMinutes, setGreaterCutoffMinutes] = useState('')
    const [methodIsCalled, setMethodIsCalled] = useState(false)

    const currentDashboardTitle = context.dashboard?.title
    const currentDashboardId = context.dashboard?.id

    function forceUpdate(): void {
        // Currently, lollipop widgets take more time to update the data after the filters are applied, so we need to force the update after 6s.
        context?.dashboard?.widgets?.forEach((widget, requestIndex) => {
            if (widget.type !== 'lollipop') {
                return
            }

            setTimeout(() => {
                context?.actions?.stop(String(requestIndex))
                context?.actions?.start(requestIndex)
            }, 6000)
        })
    }

    useEffect(() => {
        if (JSON.stringify(context?.dashboard?.widgets)?.includes('getCutoffValues')) {
            setMethodIsCalled(true)
        } else {
            return
        }
    }, [])

    useEffect(() => {
        const fetchSavedCutoffs = async (): Promise<void> => {
            try {
                const response = await getSelectedCutoffsValues(String(currentDashboardId))
                if (!response.ok) {
                    return
                }

                const parsedValue = (await response.json()) as {
                    cutoffMin?: string
                    cutoffMax?: string
                }

                const lowerValue = parsedValue.cutoffMin ?? ''
                const greaterValue = parsedValue.cutoffMax ?? ''

                setLowerCutoffSeconds(lowerValue)
                setGreaterCutoffMinutes(greaterValue)
            } catch (error) {
                console.error('Failed to load cutoffs values', error)
                setLowerCutoffSeconds('')
                setGreaterCutoffMinutes('')
            }
        }

        fetchSavedCutoffs()
    }, [currentDashboardId, currentDashboardTitle])

    useEffect(() => {
        const handleApplyClick = async (event: MouseEvent): Promise<void> => {
            const target = event.target as HTMLElement | null
            const applyButton = target?.closest('input.btn.btn-primary.accept')

            if (!applyButton || !methodIsCalled) {
                return
            }

            const lowerValue = lowerCutoffSeconds
            const greaterValue = greaterCutoffMinutes
            const dashboardId = String(currentDashboardId)

            try {
                await saveCutoffsValues(dashboardId, {
                    cutoffMin: lowerValue,
                    cutoffMax: greaterValue
                })
                forceUpdate()
            } catch (error) {
                console.error('Failed to save cutoffs values', error)
            }
        }

        document.addEventListener('click', handleApplyClick, true)

        return () => {
            document.removeEventListener('click', handleApplyClick, true)
        }
    }, [currentDashboardId, currentDashboardTitle, greaterCutoffMinutes, lowerCutoffSeconds])

    return methodIsCalled ? (
        <>
            {currentFilters}
            <FilterContainer>
                <FilterLabel>
                    <i className="fa fa-filter" style={{ color: 'var(--green-700)', marginRight: '6px' }} />
                    Exclude time lower than (seconds)
                </FilterLabel>
                <NumberInput
                    type="number"
                    placeholder="Enter value"
                    value={lowerCutoffSeconds}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLowerCutoffSeconds(event.target.value)}
                />
            </FilterContainer>

            <FilterContainer>
                <FilterLabel>
                    <i className="fa fa-filter" style={{ color: 'var(--yellow-700)', marginRight: '6px' }} />
                    Exclude time greater than (minutes)
                </FilterLabel>
                <NumberInput
                    type="number"
                    placeholder="Enter value"
                    value={greaterCutoffMinutes}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setGreaterCutoffMinutes(event.target.value)
                    }
                />
            </FilterContainer>
        </>
    ) : (
        <>{currentFilters}</>
    )
})

const FilterContainer = styled.li`
    padding: 5px 15px !important;
    white-space: normal;
    display: inline-block;
    color: var(--gray-700);
    font-size: 12px;
    line-height: 18px;
`

const FilterLabel = styled.label`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
`

const NumberInput = styled.input`
    width: 100%;
    height: 36px;
    border-radius: 6px;
    border: 1px solid var(--gray-400);
    background: linear-gradient(180deg, var(--gray-200) 0%, var(--gray-300) 100%);
    padding: 0 12px;
    font-size: 16px;
    box-sizing: border-box;
    outline: none;
    margin-bottom: 10px;
`
